import { useEffect, useState } from "react";
import { Bot, Clock, MoonStar, ClipboardCheck, Siren, MessageCircleQuestion, BellRing, PackageCheck, HeartHandshake, ShoppingBag, Cake, Star } from "lucide-react";
import { Switch } from "./ui/switch";
import api from "../lib/api";
import { toast } from "sonner";

const DAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const DEFAULT_SCHEDULE = DAYS.map((_, day) => ({
  day,
  open: "08:00",
  close: day === 5 ? "11:30" : "18:00",
  break_start: day === 5 ? "" : "11:30",
  break_end: day === 5 ? "" : "13:30",
  closed: day === 6,
}));
const timeCls = "px-2 py-1 border border-slate-300 rounded-sm text-sm";
const DEFAULT_AWAY =
  "Olá! No momento estamos fechados 🌙 Voltamos {proxima_abertura}. Deixe sua mensagem que respondemos assim que abrirmos!\n\nNosso horário:\n{horario}";

const inputCls =
  "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]";
const labelCls = "block text-xs uppercase tracking-wider text-slate-600 mb-1";

function ScheduleRow({ row, onChange }) {
  return (
    <div className="flex items-center gap-3 py-1.5" data-testid={`ai-schedule-row-${row.day}`}>
      <span className="w-20 text-sm font-medium">{DAYS[row.day]}</span>
      <Switch
        checked={!row.closed}
        onCheckedChange={(v) => onChange({ ...row, closed: !v })}
        data-testid={`ai-schedule-open-toggle-${row.day}`}
      />
      {row.closed ? (
        <span className="text-xs text-slate-400 uppercase tracking-wider">Fechado</span>
      ) : (
        <>
          <input type="time" value={row.open} onChange={(e) => onChange({ ...row, open: e.target.value })}
            data-testid={`ai-schedule-open-${row.day}`} className="px-2 py-1 border border-slate-300 rounded-sm text-sm" />
          <span className="text-xs text-slate-500">às</span>
          <input type="time" value={row.close} onChange={(e) => onChange({ ...row, close: e.target.value })}
            data-testid={`ai-schedule-close-${row.day}`} className={timeCls} />
          <span className="text-xs text-slate-500 ml-3">intervalo</span>
          <input type="time" value={row.break_start || ""} onChange={(e) => onChange({ ...row, break_start: e.target.value })}
            data-testid={`ai-schedule-break-start-${row.day}`} className={timeCls} />
          <span className="text-xs text-slate-500">às</span>
          <input type="time" value={row.break_end || ""} onChange={(e) => onChange({ ...row, break_end: e.target.value })}
            data-testid={`ai-schedule-break-end-${row.day}`} className={timeCls} />
        </>
      )}
    </div>
  );
}

function StatusBadge() {
  const [st, setSt] = useState(null);
  useEffect(() => {
    api.get("/whatsapp/hours-status").then((r) => setSt(r.data)).catch(() => {});
  }, []);
  if (!st || st.is_open === null) return null;
  return (
    <span data-testid="ai-hours-status-badge"
      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm font-semibold ${
        st.is_open ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>
      {st.is_open ? "Loja aberta agora" : `Fechada · abre ${st.next_open}`}
    </span>
  );
}

function ToggleRow({ checked, onChange, icon: Icon, label, hint, testId }) {
  return (
    <div className="flex items-start gap-3">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} className="mt-0.5" />
      <div>
        <div className="text-sm font-semibold flex items-center gap-1.5"><Icon size={15} className="text-[#2563EB]" /> {label}</div>
        <div className="text-xs text-slate-500">{hint}</div>
      </div>
    </div>
  );
}

function OwnerTestButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.post("/whatsapp/test-owner-notify");
      toast.success("Mensagem de teste enviada! Confira seu WhatsApp.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao enviar teste"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={run} disabled={busy} data-testid="settings-owner-test-btn"
      className="shrink-0 border border-slate-300 hover:bg-slate-100 disabled:opacity-50 px-3 text-xs font-semibold rounded-sm">
      {busy ? "Enviando..." : "Enviar teste"}
    </button>
  );
}

function AiActionsSettings({ s, update }) {
  return (
    <div className="border-t border-slate-100 pt-4 grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div>
          <label className={labelCls}>Seu WhatsApp pessoal (recebe avisos de OS e plantão)</label>
          <div className="flex gap-2">
            <input value={s.ai_owner_phone ?? ""} onChange={(e) => update("ai_owner_phone", e.target.value)}
              placeholder="Ex: 49999998888" data-testid="settings-ai_owner_phone" className={inputCls} />
            <OwnerTestButton />
          </div>
          <p className="text-[11px] text-slate-500 mt-1">Salve antes de testar. Use um número diferente do WhatsApp da loja.</p>
        </div>
        <ToggleRow checked={s.ai_auto_os_enabled !== false} onChange={(v) => update("ai_auto_os_enabled", v)}
          icon={ClipboardCheck} label="IA pode abrir Ordem de Serviço" testId="settings-ai_auto_os_enabled"
          hint="Quando o cliente aprova um valor da Tabela de Serviços, a IA registra a OS como Pré-agendada e te avisa." />
        <ToggleRow checked={!!s.ai_oncall_enabled} onChange={(v) => update("ai_oncall_enabled", v)}
          icon={Siren} label="Plantão fora do horário" testId="settings-ai_oncall_enabled"
          hint="Com a loja fechada, se o cliente precisar de algo urgente que a IA não resolve, ela te chama no WhatsApp." />
      </div>
      <div>
        <label className={labelCls}>Regras do plantão (a IA explica ao cliente)</label>
        <textarea rows={5} value={s.ai_oncall_notes ?? ""} onChange={(e) => update("ai_oncall_notes", e.target.value)}
          disabled={!s.ai_oncall_enabled} data-testid="settings-ai_oncall_notes"
          placeholder={"Ex:\n- Plantão até 22h.\n- Atendemos venda de carregadores, cabos e fones.\n- Taxa de plantão: R$ 10,00."}
          className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-400`} />
      </div>
    </div>
  );
}

const AUTOMATIONS = [
  { k: "ai_followup_enabled", icon: MessageCircleQuestion, label: "Retomar conversa parada",
    hint: "Se o cliente parar de responder (ex: no orçamento), a IA pergunta se ficou alguma dúvida 15–30 min depois." },
  { k: "ai_os_reminder_enabled", icon: BellRing, label: "Lembrete de OS pré-agendada",
    hint: "No dia seguinte, a partir das 8h30, lembra o cliente que ainda não trouxe o aparelho." },
  { k: "ai_ready_notice_enabled", icon: PackageCheck, label: "Aviso de OS pronta",
    hint: "Quando você muda a OS para Pronto, o cliente recebe a mensagem no WhatsApp na hora." },
  { k: "ai_checkin_enabled", icon: HeartHandshake, label: "Pós-atendimento (14 dias)",
    hint: "14 dias após a OS entregue ou a última conversa, a IA pergunta se está tudo certo (máx. 1x a cada 60 dias)." },
  { k: "ai_reservations_enabled", icon: ShoppingBag, label: "Reserva de produtos",
    hint: "A IA pode reservar um item do estoque por 24h para o cliente retirar na loja." },
  { k: "ai_birthday_enabled", icon: Cake, label: "Aniversário com cupom 10% OFF",
    hint: "Às 9h do aniversário, envia parabéns + cupom único válido por 7 dias. A IA pede a data após abrir OS/reserva." },
  { k: "ai_review_enabled", icon: Star, label: "Pedir avaliação no Google",
    hint: "Algumas horas (3h) após a OS ser marcada como Entregue, pede uma avaliação no Google. Precisa do link abaixo." },
];

function AiAutomationsSettings({ s, update }) {
  return (
    <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="settings-ai-automations">
      <div className={labelCls}>Automações da IA</div>
      <div className="grid md:grid-cols-2 gap-4">
        {AUTOMATIONS.map((a) => (
          <ToggleRow key={a.k} checked={s[a.k] !== false} onChange={(v) => update(a.k, v)}
            icon={a.icon} label={a.label} hint={a.hint} testId={`settings-${a.k}`} />
        ))}
      </div>
      <div>
        <label className={labelCls}>Link de avaliação do Google (Perfil da Empresa → Pedir avaliações)</label>
        <input value={s.google_review_url ?? ""} onChange={(e) => update("google_review_url", e.target.value)}
          placeholder="https://g.page/r/.../review" data-testid="settings-google_review_url" className={inputCls} />
      </div>
      <p className="text-xs text-slate-500">
        Mensagens automáticas não são enviadas entre 21h e 8h (exceto aviso de OS pronta). Se o cliente responder <b>SAIR</b>, ele para de receber.
      </p>
    </div>
  );
}

export function AiBotSettings({ s, update }) {
  const schedule = s.ai_schedule?.length === 7 ? s.ai_schedule : DEFAULT_SCHEDULE;
  useEffect(() => {
    if (s.ai_schedule?.length !== 7) update("ai_schedule", DEFAULT_SCHEDULE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const setRow = (row) => update("ai_schedule", schedule.map((r) => (r.day === row.day ? row : r)));

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-5" data-testid="settings-ai-section">
      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
        <div className="flex items-center gap-2">
          <Bot size={18} className="text-[#2563EB]" />
          <h2 className="font-display text-xl font-bold">IA do WhatsApp — Horário e Regras</h2>
        </div>
        <StatusBadge />
      </div>
      <p className="text-sm text-slate-500">
        A atendente virtual usa essas informações, junto com o seu estoque e a Tabela de Serviços, para responder os clientes.
      </p>

      <div>
        <div className={`${labelCls} flex items-center gap-1`}><Clock size={12} /> Horário de funcionamento (deixe o intervalo vazio se não fecha para almoço)</div>
        {schedule.map((row) => <ScheduleRow key={row.day} row={row} onChange={setRow} />)}
      </div>
      <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className={labelCls}>Observações de horário (feriados, almoço...)</label>
            <textarea rows={3} value={s.ai_business_hours ?? ""} onChange={(e) => update("ai_business_hours", e.target.value)}
              placeholder="Ex: Fechamos para almoço das 12h às 13h30. Feriados: fechado."
              data-testid="settings-ai_business_hours" className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Instruções personalizadas da IA</label>
            <textarea rows={6} value={s.ai_faq ?? ""} onChange={(e) => update("ai_faq", e.target.value)}
              placeholder={"Ex:\n- Parcelamos em até 12x no cartão.\n- Entregamos em Quilombo sem custo.\n- Película só é aplicada na loja."}
              data-testid="settings-ai_faq" className={inputCls} />
          </div>
      </div>

      <AiActionsSettings s={s} update={update} />
      <AiAutomationsSettings s={s} update={update} />

      <div className="border-t border-slate-100 pt-4 space-y-3">
        <div className="flex items-center gap-3">
          <Switch checked={!!s.ai_away_enabled} onCheckedChange={(v) => update("ai_away_enabled", v)}
            data-testid="settings-ai_away_enabled" />
          <MoonStar size={16} className="text-[#2563EB]" />
          <span className="text-sm font-semibold">Mensagem automática fora do horário</span>
        </div>
        <textarea rows={4} value={s.ai_away_message ?? DEFAULT_AWAY} onChange={(e) => update("ai_away_message", e.target.value)}
          disabled={!s.ai_away_enabled} data-testid="settings-ai_away_message" className={`${inputCls} disabled:bg-slate-50 disabled:text-slate-400`} />
        <p className="text-xs text-slate-500">
          Enviada no máximo 1 vez a cada 8h por cliente quando a loja estiver fechada. Use <code>{"{proxima_abertura}"}</code> e <code>{"{horario}"}</code> para inserir os dados automaticamente.
        </p>
      </div>
    </section>
  );
}
